import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bug, Lightbulb, Briefcase, Mail, ArrowUpRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { company } from "@/data/company";

const categories = [
  {
    title: "Bug Reports",
    description: "Something not working as expected? Report it and we'll investigate promptly.",
    icon: Bug,
    email: company.emails.company,
    subject: "Bug Report",
  },
  {
    title: "Feature Requests",
    description: "Have an idea to make our apps better? We genuinely read every suggestion.",
    icon: Lightbulb,
    email: company.emails.company,
    subject: "Feature Request",
  },
  {
    title: "Business Enquiries",
    description: "Partnerships, press or collaboration opportunities with Envago Labs.",
    icon: Briefcase,
    email: company.emails.company,
    subject: "Business Enquiry",
  },
];

const faqs = [
  {
    q: "What is Envago Labs?",
    a: "Envago Labs is an independent AI software studio building intelligent mobile applications focused on education, productivity and everyday life.",
  },
  {
    q: "What is Tanzeen+?",
    a: "Tanzeen+ is our flagship product — an AI-powered Islamic learning platform featuring the Quran, prayer times, Qibla, a Tajweed Coach AI, an AI Learning Assistant, daily Dhikr and more.",
  },
  {
    q: "Is Tanzeen+ available on the App Store or Google Play?",
    a: "Tanzeen+ is coming soon to both the Apple App Store and Google Play. You can visit tanzeenplus.com to learn more in the meantime.",
  },
  {
    q: "How do I report a bug or request a feature?",
    a: "Email us at support@envagolabs.com or use the contact form. Include as much detail as possible so we can help quickly.",
  },
  {
    q: "How can I contact support for Tanzeen+ specifically?",
    a: "For product-specific questions, email support@tanzeenplus.com. For anything about Envago Labs, use support@envagolabs.com.",
  },
  {
    q: "How does Envago Labs handle my data?",
    a: "User trust and privacy come first. We design with data responsibility and transparency at the core — see our Privacy Policy for details.",
  },
];

export default function Support() {
  return (
    <>
      <SEO
        title="Support"
        description="Get help from Envago Labs — FAQ, bug reports, feature requests and business enquiries. Contact support@envagolabs.com or support@tanzeenplus.com."
        path="/support"
      />

      <section className="relative overflow-hidden gradient-emerald">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-36 sm:px-8 md:pt-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Support
            </span>
            <h1 className="mt-5 font-display text-5xl font-light leading-none tracking-tighter md:text-6xl">
              We're here to help.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Find answers below or reach out directly. We aim to respond to every message.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact emails */}
      <section className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-2">
          <a
            href={`mailto:${company.emails.company}`}
            data-testid="support-email-company"
            className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-transform duration-300 hover:-translate-y-0.5"
          >
            <div className="flex items-center gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  General support
                </div>
                <div className="font-medium text-foreground">{company.emails.company}</div>
              </div>
            </div>
            <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
          </a>
          <a
            href={`mailto:${company.emails.tanzeen}`}
            data-testid="support-email-tanzeen"
            className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 transition-transform duration-300 hover:-translate-y-0.5"
          >
            <div className="flex items-center gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-gold/15">
                <Mail className="h-5 w-5 text-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  Tanzeen+ support
                </div>
                <div className="font-medium text-foreground">{company.emails.tanzeen}</div>
              </div>
            </div>
            <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
          </a>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-6xl px-5 pb-8 sm:px-8">
        <Stagger className="grid gap-6 md:grid-cols-3">
          {categories.map((c) => (
            <motion.a
              key={c.title}
              variants={staggerItem}
              href={`mailto:${c.email}?subject=${encodeURIComponent(c.subject)}`}
              data-testid={`support-category-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-border bg-card p-7 transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-secondary">
                <c.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="mt-5 font-display text-lg font-medium tracking-tight">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
            </motion.a>
          ))}
        </Stagger>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-3xl px-5 py-20 sm:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              FAQ
            </span>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight md:text-4xl">
              Frequently asked questions.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8">
            <Accordion type="single" collapsible className="w-full" data-testid="support-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="text-base font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 rounded-2xl border border-border bg-card p-7 text-center">
            <p className="text-base text-muted-foreground">Still need help?</p>
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
              data-testid="support-contact-link"
            >
              Send us a message
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
